import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Upload, Typography } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';

import { StyledModal } from '../../../../../components/modals/StyledModal';

import './index.scss';
import TableWrap from '../../../../../components/TableWrap';
import { CustomerIdSelectField } from './CustomIdSelectField';
import { fileExportApi, usersApi } from '../../../../../apis';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { TSearchFilter } from '../../../customerOrder/interface';
import { User } from '../../../../../apis/client-axios';
import { useInView } from 'react-intersection-observer';
import NotificationError from '../../../../../components/notifications/NotificationError';
import { useIntl } from 'react-intl';
import NotificationSuccess from '../../../../../components/notifications/NotificationSuccess';
import { ERROR_REASON_JP } from '../../../../../util/constants';

type ApiRowError = {
  rowNo: number;
  manageNo: string;
  message: string;
};

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  defaultCustomerId?: string;
  onSuccess?: () => void;
};

export const CsvBulkRegisterModal: React.FC<Props> = ({ isOpen, onCancel, defaultCustomerId, onSuccess }) => {
  const [form] = Form.useForm();

  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [rowErrors, setRowErrors] = useState<ApiRowError[]>([]);
  const hasRowErrors = rowErrors.length > 0;

  const [importResult, setImportResult] = useState<{ imported: number; total: number } | null>(null);

  const [filter, setFilter] = useState<TSearchFilter>({
    page: 1,
    size: 100000,
    sort: null,
    fullTextSearch: null,
    fullTextSearchUser: null,
    userEbayId: 0,
    locationUS: true,
    userId: 0,
    parentId: 0,
    userCode: '',
    addList: false,
    addedUserEbayList: '',
    ebayBuyerId: '',
  });

  const [inViewRefUser, inViewUser] = useInView({ threshold: 0 });
  const [totalUser, setTotalUser] = useState<number>(0);
  const [listUserOption, setListUserOption] = useState<User[]>([]);
  const [lockSubmit, setLockSubmit] = useState(false);

  const csvFileName = Form.useWatch('csvFileName', form);
  const intl = useIntl();

  const { data: listUser, fetchNextPage: fetchNextPageUser } = useInfiniteQuery({
    queryKey: ['getUser', filter.size, filter.fullTextSearchUser],
    queryFn: ({ pageParam }) =>
      usersApi.userControllerGetAll(pageParam, filter.size, filter.sort, filter.fullTextSearchUser, true),
    initialPageParam: 1,
    getNextPageParam: (_, __, page) => page + 1,
  });

  const importProductsMutation = useMutation({
    mutationFn: ({ file, userCode }: { file: File; userCode: string }) =>
      fileExportApi.fileExportControllerImportProducts(file, userCode),
  });

  const selectedFile: File | null = useMemo(() => {
    const f = uploadFileList?.[0]?.originFileObj as File | undefined;
    return f ?? null;
  }, [uploadFileList]);

  const columns: ColumnsType<ApiRowError & { key: string }> = [
    { title: '行番号', dataIndex: 'rowNo', width: 90 },
    { title: '管理番号', dataIndex: 'manageNo', width: 160 },
    { title: 'エラー内容', dataIndex: 'message' },
  ];
  const validateField = async (name: string) => {
    try {
      await form.validateFields([name]);
      return true;
    } catch {
      return false;
    }
  };

  const resetModalState = () => {
    setUploadFileList([]);
    setRowErrors([]);
    setImportResult(null);
    setLockSubmit(false);

    form.resetFields();
    form.setFieldValue('csvFileName', '');
    form.setFieldValue('customerId', defaultCustomerId ?? undefined);
  };

  const handleClose = () => {
    resetModalState();
    onCancel();
  };

  const beforeUpload = (file: File) => {
    const name = file.name.toLowerCase();

    const isCsv = file.type === 'text/csv' || name.endsWith('.csv');
    const isXlsx =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || name.endsWith('.xlsx');

    if (!isCsv && !isXlsx) {
      NotificationError({ content: intl.formatMessage({ id: 'CSVのアップロードに失敗しました' }) });
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const validateCsvRequired = async () => {
    if (!selectedFile) throw new Error('Excelファイルを選択してください。');
  };

  const onChangeFile = (fileList: UploadFile[]) => {
    setLockSubmit(false);
    const onlyOne = fileList.slice(-1);
    setUploadFileList(onlyOne);

    const file = onlyOne?.[0]?.originFileObj as File | undefined;
    form.setFieldValue('csvFileName', file?.name ?? '');

    setRowErrors([]);
    setImportResult(null);

    form
      .validateFields(['csvFileName'])
      .then(() => form.setFields([{ name: 'csvFileName', errors: [] }]))
      .catch(() => {});
  };

  function parseErrorListToRows(data: any): ApiRowError[] | null {
    const list = data?.errorList;
    if (!Array.isArray(list) || !list.length) return null;

    const rows = list.map((x: any) => {
      const reasonKey = String(x?.reason ?? '').trim();
      const msg = ERROR_REASON_JP[reasonKey] ?? reasonKey;

      return {
        rowNo: x.serialNumber,
        manageNo: String(x?.errorSku ?? ''),
        message: msg,
      };
    });

    return rows.length ? rows : null;
  }

  const onClickImport = async () => {
    if (hasRowErrors) return;

    setRowErrors([]);
    setImportResult(null);

    const okCustomer = await validateField('customerId');
    if (!okCustomer) return;

    const okFile = await validateField('csvFileName');
    if (!okFile) return;

    try {
      const customerId: string | number | undefined = form.getFieldValue('customerId');
      const pickedUser = listUserOption.find((u) => String(u.id) === String(customerId));
      const userCode = pickedUser?.userCode;

      if (!selectedFile || !userCode) {
        NotificationError({ content: intl.formatMessage({ id: 'CSVのアップロードに失敗しました' }) });
        return;
      }

      setIsSubmitting(true);

      const res = await importProductsMutation.mutateAsync({ file: selectedFile, userCode });
      const data = res?.data;

      if (data?.message === 'FILE_ERROR') {
        NotificationError({ content: intl.formatMessage({ id: 'CSVのアップロードに失敗しました' }) });
        return;
      }

      const rows = parseErrorListToRows(data);
      if (rows) {
        setRowErrors(rows);
        return;
      }

      setImportResult({
        imported: Number(data?.itemLength ?? 0),
        total: Number(data?.totalLength ?? data?.itemLength ?? 0),
      });

      NotificationSuccess({
        content: intl.formatMessage({ id: 'インポートが正常に完了しました。' }),
      });

      onSuccess?.();
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsSubmitting(false);
      setLockSubmit(true);
    }
  };

  const handleSearch = (v: { id: number | string; userCode: string }) => {
    setFilter((prev) => ({
      ...prev,
      userId: Number(v.id),
      userCode: v.userCode,
    }));
    setLockSubmit(false);
    setRowErrors([]);
    setImportResult(null);
  };

  useEffect(() => {
    if (inViewUser && listUserOption.length < totalUser) {
      fetchNextPageUser();
    }
  }, [inViewUser, listUserOption.length, totalUser, fetchNextPageUser]);

  useEffect(() => {
    if (listUser?.pages) {
      setListUserOption(listUser.pages.flatMap((page) => page?.data?.content));
      setTotalUser(listUser.pages?.[0]?.data?.total ?? 0);
    }
  }, [listUser]);

  return (
    <StyledModal
      isOpen={isOpen}
      onCancel={handleClose}
      modalProps={{
        width: 800,
        footer: null,
        className: 'csv-bulk-modal',
      }}
    >
      <div className="csv-bulk-modal__wrap">
        <Typography.Title className="csv-bulk-modal__title" level={4}>
          一括登録
        </Typography.Title>

        <Form
          form={form}
          className="csv-bulk-modal__form"
          layout="vertical"
          initialValues={{
            customerId: defaultCustomerId ?? undefined,
            csvFileName: '',
          }}
          requiredMark={false}
        >
          <Form.Item
            label={
              <span>
                <span className="form-label">顧客ID</span> <span className="required">*</span>
              </span>
            }
            name="customerId"
            validateTrigger={['onBlur', 'onChange']}
            rules={[{ required: true, message: '顧客IDを選択してください。' }]}
          >
            <CustomerIdSelectField
              form={form}
              fieldName="customerId"
              customerOptions={listUserOption.map((x) => ({
                id: x.id,
                userCode: x.userCode,
                name: x.name,
                avatarUrl: x?.avatar?.source,
              }))}
              placeholder="選択してください"
              inViewRefUser={inViewRefUser}
              handleSearch={(v) => handleSearch(v)}
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                <span className="form-label">Excelファイル</span> <span className="required">*</span>
              </span>
            }
            name="csvFileName"
            rules={[
              {
                validator: async () => {
                  await validateCsvRequired();
                },
              },
            ]}
          >
            <Input
              className="csv-bulk-modal__input"
              readOnly
              placeholder="またはドラッグ＆ドロップ"
              value={csvFileName || ''}
              addonAfter={
                <Upload
                  accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                  maxCount={1}
                  fileList={uploadFileList}
                  beforeUpload={beforeUpload}
                  onChange={({ fileList }) => onChangeFile(fileList)}
                  showUploadList={false}
                  disabled={isSubmitting}
                >
                  <Button className="csv-bulk-modal__btn-upload" disabled={isSubmitting}>
                    {selectedFile ? 'ファイルを変更' : 'ファイルを選択'}
                  </Button>
                </Upload>
              }
            />
          </Form.Item>

          <div className="csv-bulk-modal__actions">
            <Button className="btn-cancel" onClick={handleClose} disabled={isSubmitting}>
              キャンセル
            </Button>

            <Button
              className="btn-import"
              type="primary"
              loading={isSubmitting}
              onClick={onClickImport}
              disabled={isSubmitting || hasRowErrors || lockSubmit}
            >
              インポート
            </Button>
          </div>

          {importResult && (
            <div className="csv-bulk-modal__result">
              <span className="label">Import結果</span>
              <span className="colon">:</span>
              <span className="value">
                {importResult ? `${importResult.imported} / ${importResult.total}` : '0 / 0'}
              </span>
            </div>
          )}

          {rowErrors?.length > 0 && (
            <div className="csv-bulk-modal__error-table">
              <TableWrap
                columns={columns}
                data={rowErrors.map((x, idx) => ({ ...x, key: `${idx}-${x.manageNo}` }))}
                rowHoverable={false}
                isScroll
                scrollValue={{ y: 170 }}
                className="table-error"
              />
            </div>
          )}
        </Form>
      </div>
    </StyledModal>
  );
};
